/**
 * Маршруты трекера. Экраны появляются задачами эпика — пока все ведут на
 * заглушку, но адреса уже те, что будут в проде: их печатают в задачах,
 * коммитах и уведомлениях, и менять их потом дороже, чем договориться сразу.
 *
 * Проектные экраны живут под `/p/:key/…` — ключ проекта (DEV, SALES) читается
 * в адресной строке и совпадает с префиксом ключей задач.
 */
import { createRouter, createWebHistory } from 'vue-router'

import { setupAuthGuard } from '@/features/auth/guard'
import AppLayout from '@/layouts/AppLayout.vue'
import BoardView from '@/views/BoardView.vue'
import CalendarView from '@/views/CalendarView.vue'
import DevOpsEnvsView from '@/views/DevOpsEnvsView.vue'
import DevOpsFlagsView from '@/views/DevOpsFlagsView.vue'
import DevOpsPipelinesView from '@/views/DevOpsPipelinesView.vue'
import DevOpsReleasesView from '@/views/DevOpsReleasesView.vue'
import DevOpsReposView from '@/views/DevOpsReposView.vue'
import EpicsView from '@/views/EpicsView.vue'
import ForbiddenView from '@/views/ForbiddenView.vue'
import IssuesView from '@/views/IssuesView.vue'
import IssueView from '@/views/IssueView.vue'
import KnowledgeEditView from '@/views/KnowledgeEditView.vue'
import KnowledgeView from '@/views/KnowledgeView.vue'
import LoginView from '@/views/LoginView.vue'
import MyTasksView from '@/views/MyTasksView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import PlanView from '@/views/PlanView.vue'
import ProjectSettingsView from '@/views/ProjectSettingsView.vue'
import ProjectsView from '@/views/ProjectsView.vue'
import SettingsIntegrationsView from '@/views/SettingsIntegrationsView.vue'

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    { path: '/login', name: 'login', component: LoginView, meta: { public: true } },
    {
      path: '/',
      component: AppLayout,
      children: [
        // Корень ведёт на план, а не на «Мои задачи» (DEV-1656): первое, что
        // человек видит утром, — то, что он решил делать, а не всё, что на нём
        // висит.
        { path: '', redirect: '/plan' },
        {
          path: 'plan',
          name: 'plan',
          component: PlanView,
          meta: { titleKey: 'views.plan' },
        },
        {
          path: 'my',
          name: 'my-tasks',
          component: MyTasksView,
          meta: { titleKey: 'views.myTasks' },
        },
        {
          path: 'projects',
          name: 'projects',
          component: ProjectsView,
          meta: { titleKey: 'nav.projects' },
        },
        {
          path: 'calendar',
          name: 'calendar',
          component: CalendarView,
          meta: { titleKey: 'views.calendar' },
        },
        // Раздел DevOps — три экрана и мониторинг пайплайнов (DEV-1477).
        // Сам `/devops` экрана не имеет: у раздела нет сводки, только разрезы.
        { path: 'devops', redirect: '/devops/envs' },
        {
          path: 'devops/pipelines',
          name: 'devops-pipelines',
          component: DevOpsPipelinesView,
          meta: { titleKey: 'devops.pipelines.title' },
        },
        {
          path: 'devops/envs',
          name: 'devops-envs',
          component: DevOpsEnvsView,
          meta: { titleKey: 'devops.envs.title' },
        },
        {
          path: 'devops/flags',
          name: 'devops-flags',
          component: DevOpsFlagsView,
          meta: { titleKey: 'devops.flags.title' },
        },
        {
          path: 'devops/repos',
          name: 'devops-repos',
          component: DevOpsReposView,
          meta: { titleKey: 'devops.repos.title' },
        },
        {
          path: 'devops/releases',
          name: 'devops-releases',
          component: DevOpsReleasesView,
          meta: { titleKey: 'devops.releases.title' },
        },
        // База знаний: без страницы в адресе — приглашение выбрать её в дереве
        {
          path: 'kb',
          name: 'knowledge',
          component: KnowledgeView,
          meta: { titleKey: 'views.knowledge' },
        },
        {
          path: 'kb/:pageId',
          name: 'knowledge-page',
          component: KnowledgeView,
          meta: { titleKey: 'views.knowledge' },
        },
        {
          path: 'kb/:pageId/edit',
          name: 'knowledge-page-edit',
          component: KnowledgeEditView,
          meta: { titleKey: 'views.knowledge' },
        },
        // Личные настройки — не про проект, а про себя: вход из меню
        // пользователя внизу сайдбара, а не из основной навигации (ADR-0045).
        // Своей сводки у раздела нет, поэтому /settings ведёт на первый пункт.
        { path: 'settings', redirect: '/settings/integrations' },
        {
          path: 'settings/integrations',
          name: 'settings-integrations',
          component: SettingsIntegrationsView,
          meta: { titleKey: 'settings.integrations.title' },
        },
        // Задача живёт вне проекта в адресе: её ключ уже содержит проект
        // (`DEV-1421`), и `/issue/DEV-1421` короче, чем `/p/DEV/issue/DEV-1421`.
        {
          path: 'issue/:key',
          name: 'issue',
          component: IssueView,
          meta: { titleKey: 'views.issues' },
        },
        // Адрес задачи в Jira: на него ссылаются GitLab, старые письма и
        // переписка. Держим постоянно — ссылки этого вида будут приходить и
        // после того, как Jira выключат.
        { path: 'browse/:key', redirect: (to) => `/issue/${to.params.key}` },
        {
          path: 'p/:key/board',
          name: 'board',
          component: BoardView,
          meta: { titleKey: 'views.board' },
        },
        {
          path: 'p/:key/issues',
          name: 'issues',
          component: IssuesView,
          meta: { titleKey: 'views.issues' },
        },
        {
          path: 'p/:key/epics',
          name: 'epics',
          component: EpicsView,
          meta: { titleKey: 'views.epics' },
        },
        {
          path: 'p/:key/settings',
          name: 'project-settings',
          component: ProjectSettingsView,
          meta: { titleKey: 'views.settings' },
        },
        { path: '403', name: 'forbidden', component: ForbiddenView },
        // Внутри лэйаута, а не отдельным экраном: пользователь вошёл, и
        // сайдбар с навигацией — единственный способ уйти отсюда, не набирая
        // адрес руками.
        { path: ':pathMatch(.*)*', name: 'not-found', component: NotFoundView },
      ],
    },
  ],
})

setupAuthGuard(router)

export default router
